import type { ReactNode } from 'react';

/**
 * P2a — the shared page header (Roadcase pattern, maquette 006 `.mhead`).
 *
 * Mono, uppercase, tracked title on the left with an optional sub/meta line,
 * page actions on the right. It is a PRESENTATION component only: no data
 * fetching, no routing, no permissions — every page keeps owning its own
 * buttons and their gates.
 *
 * Applied in P2a to ShowsPage and TeamPage only (proof of pattern); the other
 * pages move over in P3. Class names are `rc-` prefixed so the legacy
 * `.page-header` styles that still drive the other pages are untouched.
 */
export interface PageHeaderProps {
  /** Page title. Rendered uppercase via CSS, so the accessible name stays as
   *  the caller passed it (tests can still query `Shows`). */
  title: string;
  /** Optional sub-line under the title (counts, hints, context). */
  subtitle?: ReactNode;
  /** Optional extra meta, rendered next to the subtitle. */
  meta?: ReactNode;
  /** Action buttons — rendered right-aligned, wrapping under the title on mobile. */
  actions?: ReactNode;
}

export default function PageHeader({ title, subtitle, meta, actions }: PageHeaderProps) {
  return (
    <header className="rc-header">
      <div className="rc-header-text">
        <h1 className="rc-header-title">{title}</h1>
        {(subtitle || meta) && (
          <div className="rc-header-sub">
            {subtitle}
            {meta}
          </div>
        )}
      </div>
      {actions != null && <div className="rc-header-actions">{actions}</div>}
    </header>
  );
}
